import { AgGridReact } from "ag-grid-react";
import { ColDef, ColumnState } from "ag-grid-community";
import React, { ForwardedRef, forwardRef, ReactElement, useImperativeHandle, useMemo, useRef, useState } from "react";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { defaultColDef } from "src/utils/grid/gridStyleUtils";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { pivotColumnDefs, buildPivotRows, PivotRowConfig, PivotRowData, PivotSeriesBuilder } from "src/utils/grid/gridPivotUtils";

// Grid_Chart가 받는 props 타입
interface GridChartProps<T> {
	data: T[];                              // 기간별(월/주/분기 등) 결과 리스트
	rowData: PivotRowConfig<T>[];              // 구분별 행 설정 (필드/라벨/강조 여부)
	periodLabels: string[];                 // 기간별 컬럼 헤더 라벨 목록 (예: chartUtils의 MONTH_LABELS)
	seriesBuilder: PivotSeriesBuilder<T>;   // 필드값을 기간 순서에 맞춰 추출하는 함수 (예: chartUtils의 buildMonthlySeries)
	columnStateKey: string;                 // 컬럼 상태를 로컬스토리지에 저장할 때 사용할 화면별 고유 키
	leadingColumnDefs: ColDef<PivotRowData>[]; // 기간별 값 컬럼 앞에 그대로 붙일 컬럼 목록 (구분/평균이 필요하면 divisionColumn()/averageColumn()을, 필요 없으면 빼거나 계산식 등 커스텀 ColDef를 화면에서 직접 구성해서 전달).
}

// data가 비어있을 때 defaultRowData로 사용할 빈 행 (GridContextWrapper의 필수 prop)
const EMPTY_PIVOT_ROW: PivotRowData = {
	division: "", values: [],
};

// grid_touch를 재사용하는 읽기 전용 기간별 피벗 그리드 공통 컴포넌트.
// 편집 기능(추가/삭제/붙여넣기/되돌리기)은 사용하지 않지만,
// 우클릭 메뉴의 "컬럼 초기화"/"너비 맞추기" 기능을 위해 GridContextWrapper로 감싼다.
function GridChart<T>(
	props: GridChartProps<T>,
	ref: ForwardedRef<AgGridReact<PivotRowData>>
): ReactElement {
	const { data, rowData: rows, periodLabels, seriesBuilder, columnStateKey, leadingColumnDefs } = props;

	// Grid_Touch가 필요로 하는 최소한의 내부 상태.
	// 편집 기능이 없는 읽기 전용 그리드이므로 화면별 Store 없이 컴포넌트 내부에서만 관리한다.
	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setColumnState] = useState<ColumnState[]>([]);
	const [scrollTop, setScrollTop] = useState<number | null>(null);

	// GridContextWrapper는 "컬럼 초기화" 시 null을 넘겨줄 수 있어 setColumnState가 null도 받아야 하지만,
	// Grid_Touch가 쓰는 useState 세터는 null을 받지 않으므로 어댑터로 변환한다.
	const handleSetColumnStateForWrapper = (state: ColumnState[] | null) => setColumnState(state ?? []);

	// GridContextWrapper의 우클릭 메뉴(컬럼 초기화/너비 맞추기)가 그리드 API에 접근하기 위한 내부 ref.
	// 외부에서 전달된 ref도 동일한 인스턴스를 가리키도록 연결한다.
	const internalGridRef = useRef<AgGridReact<PivotRowData>>(null);
	useImperativeHandle(ref, () => internalGridRef.current as AgGridReact<PivotRowData>, []);

	// data/rows/seriesBuilder가 바뀔 때만 재계산되는 flat 행 데이터
	const rowData = useMemo(
		() => buildPivotRows(data, rows, seriesBuilder),
		[data, rows, seriesBuilder],
	);

	// 기간 라벨/선두 컬럼이 바뀔 때만 재계산되는 컬럼 정의
	const columnDefs = useMemo(
		() => pivotColumnDefs(periodLabels, leadingColumnDefs),
		[periodLabels, leadingColumnDefs],
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(columnDefs, [])
	, [columnDefs]);

	return (
		<GridContextWrapper<PivotRowData>
			gridRef={internalGridRef}
			paddedGridData={rowData}
			initSelected={() => {}}
			defaultRowData={rowData[0] ?? EMPTY_PIVOT_ROW}
			baseColumnDefs={columnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={handleSetColumnStateForWrapper}
			localStorageKey={columnStateKey}
			menuItems={undefined}
			enableDataHistory={false}
		>
			<Grid_Touch<PivotRowData>
				ref={internalGridRef}
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				domLayout="autoHeight"
				getRowId={(params) => params.data.division}
				onCellKeyDown={undefined}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={columnStateKey}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				enableCellTextSelection={true}
			/>
		</GridContextWrapper>
	);
}

const Grid_Chart = forwardRef(GridChart) as <T>(
	props: GridChartProps<T> & { ref?: ForwardedRef<AgGridReact<PivotRowData>> }
) => ReactElement;

(Grid_Chart as React.FunctionComponent).displayName = "Grid_Chart";

export default Grid_Chart;
